/* eslint-disable max-len */
import { fromJS } from "immutable"
import urls, { getPacPaths } from "imports/desktopPaths"
import swal from "sweetalert"
import * as Sentry from "@sentry/browser"

// action creators
import { getResourceForDataType } from "shared/djangio/action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

// helpers
import { canEditNote, copyLinkToClipboard } from "shared/search/helperFunctions"
import { checkPermission, hasAnyPermission, hasAllPermissions } from "shared/imports/permissionFunctions"
import { getTransactionPath } from "app/static/frontend/pac/helperFunctions"
import { idFromResourceUri } from "shared/imports/sharedHelperFunctions"
import { isReceiptTransaction } from "shared/pac/helperFunctions"
import { stanceWithCustomLabelByValue } from "shared/stance/helperFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"

// constants
import swalContents from "app/static/frontend/swalConfigs"
import { QuorumTheme, mobileBreakpoint } from "app/static/frontend/design-constants"
import { swalContents as sC } from "app/static/frontend/search-new/components/helpTexts"

const getDownloadMenuOptions = (props) => {
    const resource = getResourceForDataType(props.quorumDataType.value)
    const objectLabel = props.quorumDataType.label
    const filters = { id: props.inlineId }
    const currentCount = 1
    const text = `This will download ${currentCount} ${objectLabel}`

    const downloadWord = {
        key: "downloadDropdownWordButton",
        iconFamily: "far",
        text: "Download Word (.doc)",
        icon: "file-word",
        onClick: async () => {
            try {
                // wait for the users input to confirm download
                const willDownload = await swal(Object.assign(swalContents.willDownload, { text }))
                // if the promise resolves to true, proceed with download
                if (willDownload) {
                    await resource.objects.filter(filters).docx().download()
                    // if all goes well, the download dialogue will show up after promise resolves
                    swal(Object.assign(swalContents.successDownload))
                }
            } catch (err) {
                swal(swalContents.downloadError)
                Sentry.captureException(err)
            }
        },
    }
    const downloadSpreadsheet = {
        key: "downloadDropdownSpreadsheetButton",
        iconFamily: "far",
        text: "Download Spreadsheet",
        icon: "table",
        onClick: async () => {
            try {
                const willDownload = await swal(Object.assign(swalContents.willDownload, { text }))
                // if the promise resolves to true, proceed with download
                if (willDownload) {
                    await resource.objects.filter(filters).csv().download()
                    // if all goes well, the download dialogue will show up after promise resolves
                    swal(Object.assign(swalContents.successDownload))
                }
            } catch (err) {
                swal(swalContents.downloadError)
                Sentry.captureException(err)
            }
        },
    }
    const downloadExcel = {
        key: "downloadDropdownExcelButton",
        iconFamily: "far",
        text: "Download Excel (.xlsx)",
        icon: "file-excel",
        onClick: async () => {
            try {
                const willDownload = await swal(Object.assign(swalContents.willDownload, { text }))
                // if the promise resolves to true, proceed with download
                if (willDownload) {
                    await resource.objects.filter(filters).xlsx().download()
                    // if all goes well, the download dialogue will show up after promise resolves
                    swal(Object.assign(swalContents.successDownload))
                }
            } catch (err) {
                swal(swalContents.downloadError)
                Sentry.captureException(err)
            }
        },
    }
    const downloadPdf = {
        key: "downloadDropdownSearchPDFButton",
        text: "Download PDF",
        icon: "file-pdf",
        iconFamily: "far",
        onClick: async () => {
            try {
                await props.createNewTemplateFromSearch({
                    filterQdt: props.quorumDataType.value,
                    filters: filters,
                    filterElementCount: currentCount,
                })
            } catch (err) {
                swal(swalContents.downloadError)
                Sentry.captureException(err)
            }
        },
    }
    return [downloadSpreadsheet, downloadWord, downloadExcel, downloadPdf]
}
const getStanceOption = (props) => {
    const currentStanceValue =
        props.appearanceValues.get("currentStance") ||
        props.datum.getIn(["_extra", "stance"]) ||
        // for FeedItems we use dehydrate_extra_priority_stance_alignment
        props.datum.getIn(["_extra", "priority_stance_alignment", "stance"]) ||
        DjangIO.app.userdata.types.LegislativeStanceType.no_stance.value
    const currentStanceEnum =
        (props.stanceOptions && stanceWithCustomLabelByValue(props.stanceOptions, currentStanceValue)) ||
        DjangIO.app.userdata.types.LegislativeStanceType.by_value(currentStanceValue)

    const stanceBaseMenuOptions = props.stanceOptions || DjangIO.app.userdata.types.LegislativeStanceType.items()

    return {
        dataCy: "stance-dropdown",
        fa5_icon: currentStanceEnum.fa5_icon,
        fa5_icon_family:
            currentStanceValue === DjangIO.app.userdata.types.LegislativeStanceType.no_stance.value ? "fal" : "fas",
        isDropdown: true,
        label: currentStanceEnum.label,
        menuOptions: stanceBaseMenuOptions
            .filter((stance) => stance.value !== currentStanceValue)
            .filter((item) => item.value)
            .sort(DjangIO.orderSort)
            .map((stance) => ({
                iconFamily: "fas",
                icon: stance.fa5_icon,
                text: stance.label,
                dataCy: stance.key,
                onClick: () => {
                    const quorumDataType = props.isFeedItem ? props.underlyingQdt : props.quorumDataType

                    props.setOrCreateStance({
                        stance: stance.value,
                        object_id: props.inlineId,
                        quorum_data_type: quorumDataType.value,
                    })

                    const appearanceInlineId = props.isFeedItem ? props.originalId : props.inlineId

                    props.updateInlineAppearance(props.uniqueFlvId, appearanceInlineId, { currentStance: stance.value })
                },
            })),
    }
}

const getPriorityOption = (props) => {
    const currentPriorityValue =
        props.appearanceValues.get("currentPriority") ||
        props.datum.getIn(["_extra", "priority"]) ||
        // for FeedItems we use dehydrate_extra_priority_stance_alignment
        props.datum.getIn(["_extra", "priority_stance_alignment", "priority"]) ||
        DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value
    const currentPriorityEnum = DjangIO.app.userdata.types.LegislativePriorityType.by_value(currentPriorityValue)
    return {
        dataCy: "priority-dropdown",
        tip_text: "Set a Priority",
        fa5_icon: currentPriorityEnum.fa5_icon,
        fa5_icon_family:
            currentPriorityValue === DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value
                ? "fal"
                : "fas",
        isDropdown: true,
        label:
            currentPriorityValue === DjangIO.app.userdata.types.LegislativePriorityType.no_priority.value
                ? currentPriorityEnum.label
                : currentPriorityEnum.label.replace(" Priority", ""),
        menuOptions: DjangIO.app.userdata.types.LegislativePriorityType.items()
            .filter((priority) => priority.value !== currentPriorityValue)
            .filter((item) => item.value)
            .map((priority) => ({
                iconFamily: "fas",
                icon: priority.fa5_icon,
                text: priority.label.replace(" Priority", ""),
                dataCy: priority.key,
                onClick: () => {
                    const quorumDataType = props.isFeedItem ? props.underlyingQdt : props.quorumDataType

                    props.setOrCreatePriority({
                        priority: priority.value,
                        object_id: props.inlineId,
                        quorum_data_type: quorumDataType.value,
                    })

                    const appearanceInlineId = props.isFeedItem ? props.originalId : props.inlineId

                    props.updateInlineAppearance(props.uniqueFlvId, appearanceInlineId, {
                        currentPriority: priority.value,
                    })
                },
            }))
            .sort(DjangIO.valueSort),
    }
}

const getAlignmentOption = (props) => {
    const alignmentId =
        props.appearanceValues.get("currentAlignment") ||
        props.datum.getIn(["_extra", "alignment"]) ||
        // for FeedItems we use dehydrate_extra_priority_stance_alignment
        props.datum.getIn(["_extra", "priority_stance_alignment", "alignment"])
    const currentAlignment =
        props.alignmentTypes.find((alignment) => alignment.get("id") === alignmentId) ||
        fromJS({ label: "No Alignment", icon: "user-plus", iconFamily: "fal", id: 0 })
    return {
        dataCy: "alignment-dropdown",
        fa5_icon: currentAlignment.get("icon"),
        fa5_icon_family: currentAlignment.get("iconFamily"),
        isDropdown: true,
        label: currentAlignment.get("label"),
        menuOptions: props.alignmentTypes
            .filter((alignment) => alignment.get("id") !== alignmentId)
            .map((alignment) => ({
                iconFamily: "fas",
                icon: alignment.get("icon"),
                text: alignment.get("label"),
                dataCy: alignment.get("label"),
                onClick: () => {
                    const quorumDataType = props.isFeedItem ? props.underlyingQdt : props.quorumDataType

                    props.postProfileAlignment({
                        qdt: quorumDataType,
                        profileId: props.inlineId,
                        alignmentTypeId: alignment.get("id"),
                    })

                    const appearanceInlineId = props.isFeedItem ? props.originalId : props.inlineId

                    props.updateInlineAppearance(props.uniqueFlvId, appearanceInlineId, {
                        currentAlignment: alignment.get("id"),
                    })
                },
            })),
    }
}

export const eventOptionsSelector = (props) => [
    {
        fa5_icon: "trash-alt",
        fa5_icon_family: "fas",

        tip_text: "Delete Event",
        icon: "fa-trash",
        display: props.datum.get("event_type") === DjangIO.app.events.models.EventType.custom.value,
        href: "#",
        callback: (e) => {
            props.deleteEvent(props.datum, props.uniqueFlvId, props.inlineId)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        fa5_icon: "pencil-alt",
        fa5_icon_family: "fas",

        tip_text: "Edit Event",
        icon: "fa-pencil-square-o",
        display: props.datum.get("event_type") === DjangIO.app.events.models.EventType.custom.value,
        href: `${urls.editEventPrefix}${props.inlineId}/`,
        callback: (e) => {
            props.segueToEditEvent(props.inlineId)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        fa5_icon: "calendar-plus",
        fa5_icon_family: "fas",

        tip_text: "Download to Calendar",
        icon: "fa-calendar-plus-o",
        callback: (e) => {
            e.stopPropagation()
            const headers = new Headers({ "x-csrftoken": Userdata.csrftoken })
            fetch(`/api/event/${props.inlineId}/?ics=true`, { headers, mode: "same-origin" })
        },
    },
    {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
]

export const documentOptionsSelector = (props) => {
    const optionsArray = []

    // Get document type
    const documentType = props.datum.get("document_type")

    const sourceCommittee = props.datum.get("source_committee")
    // get boolean value of 'committee_meeting__generate_manual_transcript' from dehydrate_extra_committee_meeting
    const sourceManualTranscript =
        props.datum.getIn(["_extra", "committee_meeting", "committee_meeting__generate_manual_transcript"]) ||
        props.datum.get("committee_meeting")

    // in Search, source_committee is a string referencing the resource URI.
    // in Document profiles, source_committee is the dehydrated source committee object. (document-action-creators.js)
    const sourceCommitteeId =
        sourceCommittee &&
        (typeof sourceCommittee === "string" ? idFromResourceUri(sourceCommittee) : sourceCommittee.get("id"))

    const sourceCustomOrganizationId = props.datum.get("source_custom_organization")

    const sourcePerson = props.datum.get("source_person")
    const sourcePersonId = sourcePerson && idFromResourceUri(sourcePerson)

    const sourcePublicOrganization = props.datum.get("source_public_organization")
    const sourcePublicOrganizationId = sourcePublicOrganization && idFromResourceUri(sourcePublicOrganization)

    const sourceSupporterId = props.datum.get("source_supporter")

    const sourceTypeEnum = DjangIO.app.document.models.DocumentSourceType.by_value(props.datum.get("source_type"))

    if (sourcePersonId) {
        const sourceType = DjangIO.app.document.models.DocumentSourceType.by_value(props.datum.get("source_type"))
        const sourceQdt = DjangIO.app.models.QuorumDataType.by_value(sourceType.data_type)

        optionsArray.push({
            fa5_icon: "user-plus",
            fa5_icon_family: "fas",

            tip_text: `Source ${sourceQdt.singular}`,
            icon: "fa-user-plus",
            href: `${sourceQdt.profile}${sourcePersonId}`,
            callback: (e) => {
                props.segueToProfile(
                    {
                        id: sourcePersonId,
                    },
                    sourceQdt.value,
                )
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    if (sourceCommitteeId) {
        optionsArray.push({
            fa5_icon: sourceTypeEnum.fa5_icon,
            fa5_icon_family: sourceTypeEnum.fa5_icon_family,

            tip_text: "Source Committee",
            icon: DjangIO.app.models.QuorumDataType.committee.icon,
            href: `${DjangIO.app.models.QuorumDataType.committee.profile}${sourceCommitteeId}`,
            callback: (e) => {
                props.segueToProfile(
                    {
                        id: sourceCommitteeId,
                    },
                    DjangIO.app.models.QuorumDataType.committee.value,
                )
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    if (sourcePublicOrganizationId) {
        optionsArray.push({
            fa5_icon: sourceTypeEnum.fa5_icon,
            fa5_icon_family: sourceTypeEnum.fa5_icon_family,

            tip_text: "Source Public Organization",
            icon: DjangIO.app.models.QuorumDataType.public_organization.icon,
            href: `${DjangIO.app.models.QuorumDataType.public_organization.profile}${sourcePublicOrganizationId}`,
            callback: (e) => {
                props.segueToProfile(
                    {
                        id: sourcePublicOrganizationId,
                    },
                    DjangIO.app.models.QuorumDataType.public_organization.value,
                )
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    if (sourceCustomOrganizationId) {
        optionsArray.push({
            fa5_icon: sourceTypeEnum.fa5_icon,
            fa5_icon_family: sourceTypeEnum.fa5_icon_family,

            tip_text: "Source Custom Organization",
            icon: DjangIO.app.models.QuorumDataType.public_organization.icon,
            href: `${DjangIO.app.models.QuorumDataType.public_organization.profile}${sourceCustomOrganizationId}`,
            callback: (e) => {
                props.segueToProfile(
                    {
                        id: sourceCustomOrganizationId,
                    },
                    DjangIO.app.models.QuorumDataType.public_organization.value,
                )
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    if (sourceSupporterId) {
        optionsArray.push({
            fa5_icon: sourceTypeEnum.fa5_icon,
            fa5_icon_family: sourceTypeEnum.fa5_icon_family,

            tip_text: "Source Contact",
            icon: DjangIO.app.models.QuorumDataType.supporter.icon,
            href: `${DjangIO.app.models.QuorumDataType.supporter.profile}${sourceSupporterId}`,
            callback: (e) => {
                props.segueToProfile(
                    {
                        id: sourceSupporterId,
                    },
                    DjangIO.app.models.QuorumDataType.supporter.value,
                )
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    optionsArray.push({
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: "fa-list",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    })

    // Hide issues button in card view if the docuemnt type is committee_transcript and we expect verified transcript for this committee
    if (
        !(
            sourceManualTranscript &&
            documentType === DjangIO.app.document.models.DocumentType.committee_transcript.value
        )
    ) {
        optionsArray.push({
            fa5_icon: "thumbtack",
            fa5_icon_family: "fas",

            tip_text: "Issues",
            icon: "fa-thumb-tack",
            parentId: props.inlineId,
            callback: (e) => {
                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                    listDropDownEnabled: false,
                    issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
                })
                e.stopPropagation()
            },
        })
    }

    // Hide stance button in card view if the docuemnt type is committee_transcript and we expect verified transcript for this committee
    if (
        !(
            sourceManualTranscript &&
            documentType === DjangIO.app.document.models.DocumentType.committee_transcript.value
        )
    ) {
        optionsArray.push({
            ...getStanceOption(props),

            tip_text: "Take a Stance",
            dataCy: "take-a-stance",
            icon: "fa-thumbs-o-up",
            parentId: props.inlineId,
            callback: (e) => {
                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                    issueDropDownEnabled: false,
                    listDropDownEnabled: false,
                    priorityDropDownEnabled: false,
                    stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
                })
                e.stopPropagation()
            },
        })
    }

    return optionsArray
}

export const donationTypeValues = [
    DjangIO.app.userdata.types.InteractionType.donation_given.value,
    DjangIO.app.userdata.types.InteractionType.donation_received.value,
    DjangIO.app.userdata.types.InteractionType.intended_donation.value,
    DjangIO.app.userdata.types.InteractionType.sponsorship.value,
    DjangIO.app.userdata.types.InteractionType.grant.value,
]

export const noteOptionsSelector = (props) => {
    const isDonation = donationTypeValues.includes(props.datum.get("interaction_type"))
    const isRelationship = Boolean(props.datum.get("relationship_type"))
    const isComment = Boolean(props.datum.get("comment_type"))

    const goToNewLoggers = (e) => {
        if (isRelationship) {
            props.segueToEditRelationship(props.inlineId)
        } else if (isComment) {
            props.segueToEditComment(props.inlineId)
        } else if (isDonation) {
            props.segueToEditDonation(props.inlineId)
        } else {
            props.segueToEditInteraction(props.inlineId)
        }
        e.stopPropagation()
        e.preventDefault()
    }

    const goToNewLoggersUrl = () => {
        const interactionURI = []

        if (isRelationship) {
            interactionURI.push(urls.logRelationshipPrefix)
        } else if (isComment) {
            interactionURI.push(urls.logCommentPrefix)
        } else if (isDonation) {
            interactionURI.push(urls.logDonationPrefix)
        } else {
            interactionURI.push(urls.logInteractionPrefix)
        }

        interactionURI.push(`${props.inlineId}/`)

        return interactionURI.join("")
    }

    const optionsArray = []

    const interactionType =
        props.datum.get("interaction_type") &&
        DjangIO.app.userdata.types.InteractionType.by_value(props.datum.get("interaction_type"))

    if (canEditNote(interactionType, props.datum.get("user_id"), props.datum.get("auto_created"))) {
        optionsArray.push({
            fa5_icon: "pen",
            fa5_icon_family: "far",

            tip_text: props.datum.get("from_grassroots") ? "Edit Grassroots Note" : "Edit Note",
            icon: "fa-pencil",
            href: goToNewLoggersUrl(),
            callback: goToNewLoggers,
        })
    }

    optionsArray.push({
        fa5_icon: "copy",
        fa5_icon_family: "far",

        tip_text: "Copy link",
        icon: "fa-copy",
        callback: () => copyLinkToClipboard(window.location.origin + "/search/notes?id__in=" + props.datum.get("id")),
    })

    const approveTipText = "Approve & Send"

    // display the action center approve button if we are in grassroots and the action has not been approved AND not delivered.
    // If delivered, we treat action as if it has been approved
    if (
        props.datum.getIn(["_extra", "campaign", "require_confirmation"]) &&
        !props.datum.get("confirmed") &&
        !props.datum.get("delivered")
    ) {
        optionsArray.push({
            fa5_icon: "check",
            fa5_icon_family: "fas",

            tip_text: approveTipText,
            icon: "fa-check",
            text: "Approve",
            callback: (e) => {
                props.updateSupporterAction(props.datum, props.uniqueFlvId, props.quorumDataType.value)
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    // if interaction, provide download
    if (
        !props.isMobile &&
        props.datum.get("note_type") === DjangIO.app.userdata.types.NoteType.interaction.value &&
        props.datum.get("from_grassroots") === false
    ) {
        optionsArray.push({
            fa5_icon: "calendar-day",
            fa5_icon_family: "far",

            tip_text: "Download to Calendar",
            icon: "fa-calendar-plus-o",
            callback: (e) => {
                e.stopPropagation()
                const headers = new Headers({ "x-csrftoken": Userdata.csrftoken })
                fetch(`/api/note/${props.inlineId}/?ics=true`, { headers, mode: "same-origin" })
                    .then((res) => res.blob())
                    .then((blob) => {
                        const file = window.URL.createObjectURL(blob)
                        window.location.assign(file)
                    })
            },
        })
    }

    // single note download
    const downloadMenuOptions = getDownloadMenuOptions(props)

    // Downloading a single Note doesn't make sense on a phone, and the dropdown would take time to implement properly.
    if (!props.isMobile) {
        optionsArray.push({
            fa5_icon: "arrow-to-bottom",
            fa5_icon_family: "far",
            isDropdown: true,
            tip_text: "Download Note",
            icon: "fa-download",
            menuOptions: downloadMenuOptions,
        })
    }

    // delete the note/grassroots action
    const deleteIcon = "fa-trash"
    const deleteGrassrootsActionTipText = "Delete Grassroots Action"
    if (
        DjangIO.app.userdata.types.GrassrootsInteractionType.filter_groups
            .find((group) => group.title === "Grassroots")
            .values.includes(props.datum.get("interaction_type"))
    ) {
        optionsArray.push({
            fa5_icon: "trash",
            fa5_icon_family: "far",
            iconColor: QuorumTheme.colors.red[9],

            tip_text: deleteGrassrootsActionTipText,
            icon: deleteIcon,
            callback: (e) => {
                props.deleteGrassrootsAction(props.datum, props.uniqueFlvId, props.inlineId)
                e.stopPropagation()
                e.preventDefault()
            },
        })
    } else {
        optionsArray.push({
            fa5_icon: "trash",
            fa5_icon_family: "far",
            iconColor: QuorumTheme.colors.red[9],

            tip_text: "Archive Note",
            icon: deleteIcon,
            callback: (e) => {
                props.deleteNote(props.datum, props.uniqueFlvId, props.inlineId)
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    const isVideo = Boolean(props.datum.get("share_story_video_uuid"))
    if (isVideo) {
        // hide the approve and delete grassroots action buttons on video notes
        // because this will be at the right section, bellow thumbnail
        const buttonsToHideOnVideo = [approveTipText, deleteGrassrootsActionTipText]
        return optionsArray.filter((option) => !buttonsToHideOnVideo.includes(option.tip_text))
    }

    return optionsArray
}

export const billOptionsSelector = (props) => {
    if (props.isMobile) {
        return []
    }

    const edit = {
        fa5_icon: "pencil-alt",
        fa5_icon_family: "fas",

        href: `${urls.editBillPrefix}${props.inlineId}/`,
        tip_text: "Edit Custom Fields",
        icon: "fa-pencil",
        parentId: props.inlineId,
        callback: (e) => {
            props.segueToEditBill(props.inlineId)
            e.stopPropagation()
            e.preventDefault()
        },
    }
    const stance = {
        ...getStanceOption(props),

        tip_text: "Take a Stance",
        dataCy: "take-a-stance",
        icon: "fa-thumbs-o-up",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: false,
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
            })
            e.stopPropagation()
        },
    }
    const priority = {
        ...getPriorityOption(props),

        tip_text: "Set a Priority",
        icon: "fa-sort-numeric-asc",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: !props.appearanceValues.get("priorityDropDownEnabled"),
                listDropDownEnabled: false,
                issueDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    }
    const lists = {
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: "fa-list",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    }
    const issues = {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: false,
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    }
    const { PermissionType } = DjangIO.app.models
    if (hasAllPermissions([PermissionType.hide_stance, PermissionType.hide_priority])) {
        return [edit, lists, issues]
    } else if (hasAnyPermission([PermissionType.hide_stance])) {
        return [edit, lists, issues, priority]
    } else if (hasAnyPermission([PermissionType.hide_priority])) {
        return [edit, lists, issues, stance]
    }
    return [edit, lists, issues, stance, priority]
}

export const voteOptionsSelector = (props) => [
    {
        ...getStanceOption(props),

        tip_text: "Take a Stance",
        icon: "fa-thumbs-o-up",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: false,
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: " fa-list",
        parentId: props.inlineId,
        dataFunction: "toggleLists",
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
]

export const amendmentOptionsSelector = (props) => [
    {
        fa5_icon: "user-plus",
        fa5_icon_family: "fas",

        href: `/official/${props.datum.get("sponsor_id")}/`,
        tip_text: "Sponsor Details",
        icon: "fa-user-plus",
        callback: (e) => {
            props.segueToProfile({ id: props.datum.get("sponsor_id") }, DjangIO.app.models.QuorumDataType.person.value)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: "fa-list",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
]

export const fileOptionsSelector = (props) => [
    {
        fa5_icon: "download",
        fa5_icon_family: "fas",

        tip_text: "Download File",
        icon: "fa-download",
        href: `${urls.outboxDownloadPrefix}${props.inlineId}/`,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        callback() {},
    },
    {
        fa5_icon: "pencil-alt",
        fa5_icon_family: "fas",
        tip_text: "Update File",
        icon: "fa-pencil-alt",
        isFileInput: true,
        callback: (e) => {
            const { files } = e.target
            // if there are not files from the input, the user cancelled
            if (!files || !files.length) {
                return
            }

            const data = new FormData()
            for (let key = 0; key < files.length; key += 1) {
                const file = files[key]
                data.append(key, file)
            }

            data.append("id", props.datum.get("id"))
            const successCallback = () => {
                swal(sC.successReplaceFile)
                sendUserActionPost({
                    userActionType: DjangIO.app.useractions.types.UserActionType.file_uploaded,
                })
                props.triggerDataRefresh(props.uniqueFlvId, DjangIO.newResourceFromDataType(props.quorumDataType.value))
            }
            props.updateFile(data, successCallback)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        fa5_icon: "trash-alt",
        fa5_icon_family: "fas",

        tip_text: "Delete File",
        icon: "fa-trash",
        callback: (e) => {
            props.deleteFile(props.datum, props.uniqueFlvId, props.inlineId)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        fa5_icon: "link",
        fa5_icon_family: "fas",
        tip_text: "Copy Source Link",
        icon: "fa-link",
        callback: () => copyLinkToClipboard(props.datum.get("temp_s3_url")),
    },
    {
        fa5_icon: "external-link",
        fa5_icon_family: "fas",
        tip_text: "View File",
        icon: "fa-external-link",
        callback: () => window.open(props.datum.get("temp_s3_url"), "_blank").focus(),
    },
]

export const organizationOptionsSelector = (props) => {
    const options = []
    const isCustomOrg =
        props.datum.get("organization_type") === DjangIO.app.person.types.PublicOrganizationType.custom.value
    const isOutlet = props.datum.get("_ph_sector_type") === DjangIO.app.person.newtypes.SectorType.media.value

    // we only want to show the Edit Custom Fields inline option for Custom Orgs and Outlets
    if (isCustomOrg) {
        options.push(
            {
                fa5_icon: "pencil-alt",
                fa5_icon_family: "fas",

                href: `${urls.editOrganizationPrefix}${props.inlineId}/`,
                tip_text: "Edit Custom Fields",
                icon: "fa-pencil",
                parentId: props.inlineId,
                callback: (e) => {
                    props.segueToEditOrganization(props.inlineId)
                    e.stopPropagation()
                    e.preventDefault()
                },
            },
            {
                fa5_icon: "trash-alt",
                fa5_icon_family: "fas",

                tip_text: "Delete Organization",
                icon: "fa-trash",
                href: "#",
                callback: (e) => {
                    props.deletePublicOrganization(props.datum, props.uniqueFlvId, props.inlineId)
                    e.stopPropagation()
                    e.preventDefault()
                },
            },
        )
    } else {
        options.push({
            fa5_icon: "pencil-alt",
            fa5_icon_family: "fas",

            href: `${urls.modifyOrganizationPrefix}${props.inlineId}/`,
            tip_text: "Edit Custom Fields",
            icon: "fa-pencil",
            parentId: props.inlineId,
            callback: (e) => {
                props.segueToModifyOrganization(props.inlineId)
                e.stopPropagation()
                e.preventDefault()
            },
        })

        if (isOutlet) {
            options.push({
                fa5_icon: "list-ul",
                fa5_icon_family: "fas",

                tip_text: "Lists",
                icon: " fa-list",
                parentId: props.inlineId,
                callback: (e) => {
                    props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                        issueDropDownEnabled: false,
                        listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                        priorityDropDownEnabled: false,
                        stanceDropDownEnabled: false,
                    })
                    e.stopPropagation()
                },
            })
        }
    }

    options.push({
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    })
    return options
}

export const officialOptionsSelector = (props) => {
    const isCustomOfficial = !!(props.datum.get("organization") || props.datum.get("user"))
    const shouldHideEditButton = isCustomOfficial && !Userdata.is_admin
    const { PermissionType } = DjangIO.app.models
    return props.isMobile
        ? []
        : [
              ...(isCustomOfficial
                  ? []
                  : [
                        {
                            tip_text: "Suggest Edit",
                            icon: "fa-flag",
                            fa5_icon: "flag",
                            fa5_icon_family: "fas",
                            parentId: props.inlineId,
                            callback: (event) => {
                                props.requestChanges(props.inlineId)

                                event.stopPropagation()
                                event.preventDefault()
                            },
                        },
                    ]),
              ...(shouldHideEditButton
                  ? []
                  : [
                        {
                            fa5_icon: "pencil-alt",
                            fa5_icon_family: "fas",

                            href: `${urls.editOfficialPrefix}${props.inlineId}/`,
                            tip_text: isCustomOfficial ? "Edit Custom Official" : "Edit Custom Fields",
                            icon: " fa-pencil",
                            parentId: props.inlineId,
                            callback: (e) => {
                                isCustomOfficial
                                    ? props.segueToEditCustomOfficial(props.inlineId)
                                    : props.segueToEditOfficial(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]),
              {
                  fa5_icon: "download",
                  fa5_icon_family: "fas",

                  tip_text: "Download",
                  icon: "fa-download",
                  href: DjangIO.app.person.models.Person.objects.get(props.inlineId).action("vcf").buildUrl(),
                  callback: (e) => {
                      e.stopPropagation()

                      if (isFeatureEnabled("ff_new_card_square")) {
                          document.location.href = DjangIO.app.person.models.Person.objects
                              .get(props.inlineId)
                              .action("vcf")
                              .buildUrl()
                      }
                  },
              },
              ...(checkPermission(DjangIO.app.models.PermissionType.officials_outbox) &&
              props.datum.get("email") &&
              props.datum.get("valid_email") &&
              [
                  DjangIO.app.person.types.RoleType.senator.value,
                  DjangIO.app.person.types.RoleType.representative.value,
              ].indexOf(props.datum.get("most_recent_role_type")) === -1
                  ? [
                        {
                            fa5_icon: "envelope",
                            fa5_icon_family: "fas",

                            tip_text: "Outbox",
                            icon: "fa-envelope",
                            href: `${urls.outboxCompose}?toPeople=[${props.inlineId}]`,
                            callback: (e) => {
                                props.segueToOutboxCompose(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              {
                  fa5_icon: "thumbtack",
                  fa5_icon_family: "fas",

                  tip_text: "Issues",
                  icon: "fa-thumb-tack",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                          listDropDownEnabled: false,
                          issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
                          alignmentDropDownEnabled: false,
                      })
                      e.stopPropagation()
                  },
              },
              {
                  fa5_icon: "list-ul",
                  fa5_icon_family: "fas",

                  tip_text: "Lists",
                  icon: " fa-list",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                          issueDropDownEnabled: false,
                          listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                          priorityDropDownEnabled: false,
                          stanceDropDownEnabled: false,
                      })
                      e.stopPropagation()
                  },
              },
              ...(hasAnyPermission([PermissionType.hide_alignment])
                  ? []
                  : [
                        {
                            ...getAlignmentOption(props),
                            tip_text: "Stakeholder Alignment",
                            icon: "fa-user-plus",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                                    listDropDownEnabled: false,
                                    issueDropDownEnabled: false,
                                    alignmentDropDownEnabled: !props.appearanceValues.get("alignmentDropDownEnabled"),
                                })
                                e.stopPropagation()
                            },
                        },
                    ]),
              ...(hasAnyPermission([PermissionType.hide_priority])
                  ? []
                  : [
                        {
                            ...getPriorityOption(props),

                            tip_text: "Set a Priority",
                            icon: "fa-sort-numeric-asc",
                            parentId: props.inlineId,
                            callback: (event) => {
                                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                                    priorityDropDownEnabled: !props.appearanceValues.get("priorityDropDownEnabled"),
                                    listDropDownEnabled: false,
                                    issueDropDownEnabled: false,
                                })
                                event.stopPropagation()
                            },
                        },
                    ]),
          ]
}

export const pressContactOptionsSelector = (props) =>
    props.isMobile
        ? []
        : [
              {
                  fa5_icon: "pencil-alt",
                  fa5_icon_family: "fas",

                  href: `${urls.editPressContactPrefix}${props.inlineId}/`,
                  tip_text: "Edit Custom Fields",
                  icon: " fa-pencil",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.segueToEditPressContact(props.inlineId)
                      e.stopPropagation()
                      e.preventDefault()
                  },
              },
              {
                  fa5_icon: "download",
                  fa5_icon_family: "fas",

                  tip_text: "Download",
                  icon: "fa-download",
                  href: DjangIO.app.person.models.Person.objects.get(props.inlineId).action("vcf").buildUrl(),
                  callback: (e) => {
                      e.stopPropagation()

                      if (isFeatureEnabled("ff_new_card_square")) {
                          document.location.href = DjangIO.app.person.models.Person.objects
                              .get(props.inlineId)
                              .action("vcf")
                              .buildUrl()
                      }
                  },
              },
              ...(checkPermission(DjangIO.app.models.PermissionType.media_relations) &&
              props.datum.get("email") &&
              props.datum.get("valid_email")
                  ? [
                        {
                            fa5_icon: "envelope",
                            fa5_icon_family: "fas",

                            tip_text: "Outbox",
                            icon: "fa-envelope",
                            href: `${urls.outboxCompose}?toPeople=[${props.inlineId}]`,
                            callback: (e) => {
                                props.segueToOutboxCompose(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              {
                  fa5_icon: "list-ul",
                  fa5_icon_family: "fas",

                  tip_text: "Lists",
                  icon: " fa-list",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                          issueDropDownEnabled: false,
                          listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                          priorityDropDownEnabled: false,
                          stanceDropDownEnabled: false,
                      })
                      e.stopPropagation()
                  },
              },
              {
                  fa5_icon: "thumbtack",
                  fa5_icon_family: "fas",

                  tip_text: "Issues",
                  icon: "fa-thumb-tack",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                          listDropDownEnabled: false,
                          issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
                          alignmentDropDownEnabled: false,
                      })
                      e.stopPropagation()
                  },
              },
              {
                  fa5_icon: "user-plus",
                  fa5_icon_family: "fas",

                  tip_text: "Stakeholder Alignment",
                  icon: "fa-user-plus",
                  parentId: props.inlineId,
                  callback: (e) => {
                      props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                          listDropDownEnabled: false,
                          issueDropDownEnabled: false,
                          alignmentDropDownEnabled: !props.appearanceValues.get("alignmentDropDownEnabled"),
                      })
                      e.stopPropagation()
                  },
              },
          ]

export const supporterOptionsSelector = (props) =>
    window.innerWidth <= mobileBreakpoint
        ? [
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
                  ? [
                        {
                            fa5_icon: "pen",
                            fa5_icon_family: "far",

                            tip_text: "Edit Contact",
                            icon: "fa-pencil",
                            href: `${urls.editContactPrefix}${props.inlineId}/`,
                            callback: (e) => {
                                props.segueToEditContact(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
                  ? [
                        {
                            fa5_icon: "trash",
                            fa5_icon_family: "far",
                            iconColor: QuorumTheme.colors.red[9],

                            tip_text: "Delete Contact",
                            icon: "fa-trash",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.deleteContact(props.datum, props.uniqueFlvId, props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
          ]
        : [
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
                  ? [
                        {
                            fa5_icon: "pencil-alt",
                            fa5_icon_family: "fas",

                            tip_text: "Edit Contact",
                            icon: "fa-pencil",
                            href: `${urls.editContactPrefix}${props.inlineId}/`,
                            callback: (e) => {
                                props.segueToEditContact(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
                  ? [
                        {
                            fa5_icon: "trash-alt",
                            fa5_icon_family: "fas",

                            tip_text: "Delete Contact",
                            icon: "fa-trash",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.deleteContact(props.datum, props.uniqueFlvId, props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_outbox) && props.datum.get("email")
                  ? [
                        {
                            fa5_icon: "envelope",
                            fa5_icon_family: "fas",

                            tip_text: "Outbox",
                            icon: "fa-envelope",
                            edit_mode: false,
                            href: `${urls.outboxCompose}?toSupporters=[${props.inlineId}]`,
                            callback: (e) => {
                                props.segueToOutboxComposeSupporter(props.inlineId)
                                e.stopPropagation()
                                e.preventDefault()
                            },
                        },
                    ]
                  : []),
              ...(checkPermission(DjangIO.app.models.PermissionType.custom_contacts)
                  ? [
                        {
                            fa5_icon: "download",
                            fa5_icon_family: "fas",

                            tip_text: "Download",
                            icon: "fa-download",
                            href: DjangIO.app.grassroots.models.Supporter.objects
                                .get(props.inlineId)
                                .action("vcf")
                                .buildUrl(),
                            callback: (e) => {
                                e.stopPropagation()

                                if (isFeatureEnabled("ff_new_card_square")) {
                                    document.location.href = DjangIO.app.grassroots.models.Supporter.objects
                                        .get(props.inlineId)
                                        .action("vcf")
                                        .buildUrl()
                                }
                            },
                        },
                        {
                            fa5_icon: "list-ul",
                            fa5_icon_family: "fas",

                            tip_text: "Lists",
                            icon: "fa-list",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                                    issueDropDownEnabled: false,
                                    listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                                    priorityDropDownEnabled: false,
                                    stanceDropDownEnabled: false,
                                })
                                e.stopPropagation()
                            },
                        },
                        {
                            fa5_icon: "thumbtack",
                            fa5_icon_family: "fas",

                            tip_text: "Issues",
                            icon: "fa-thumb-tack",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                                    listDropDownEnabled: false,
                                    issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
                                })
                                e.stopPropagation()
                            },
                        },
                        {
                            ...getAlignmentOption(props),

                            tip_text: "Stakeholder Alignment",
                            icon: "fa-user-plus",
                            parentId: props.inlineId,
                            callback: (e) => {
                                props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                                    listDropDownEnabled: false,
                                    issueDropDownEnabled: false,
                                    alignmentDropDownEnabled: !props.appearanceValues.get("alignmentDropDownEnabled"),
                                })
                                e.stopPropagation()
                            },
                        },
                        {
                            ...getPriorityOption(props),
                        },
                    ]
                  : []),
          ]

export const regulationOptionsSelector = (props) => [
    {
        fa5_icon: "pencil-alt",
        fa5_icon_family: "fas",

        href: `${urls.editRegulationPrefix}${props.inlineId}/`,
        tip_text: "Edit Custom Fields",
        icon: "fa-pencil",
        parentId: props.inlineId,
        callback: (e) => {
            props.segueToEditRegulation(props.inlineId)
            e.stopPropagation()
            e.preventDefault()
        },
    },
    {
        ...getStanceOption(props),

        tip_text: "Take a Stance",
        icon: "fa-thumbs-o-up",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: false,
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
    {
        ...getPriorityOption(props),

        tip_text: "Set a Priority",
        icon: "fa-sort-numeric-asc",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: !props.appearanceValues.get("priorityDropDownEnabled"),
                listDropDownEnabled: false,
                issueDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: " fa-list",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: false,
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
]

export const liveTranscriptSectionOptionsSelector = (props) => [
    {
        fa5_icon: "play",
        fa5_icon_family: "fas",

        tip_text: "Show in Video",
        icon: "fa-play",
        parentId: props.inlineId,
        callback: (e) => {
            props.onClickHandler(props.datum.get("id"))
        },
    },
]

export const transactionOptionsSelector = (props) => {
    const id = props.datum.get("id")
    const ledgerSettingsId = props.datum.get("ledger_settings_id")
    const transactionType = props.datum.get("transaction_type")
    const isMemoEntry = props.datum.get("is_memo_entry")
    const referenceTransactionUri = props.datum.get("reference_transaction")
    const typeLabel = isReceiptTransaction(transactionType) ? "receipt" : "disbursement"

    const editTransactionUrl = getTransactionPath({
        id,
        ledgerSettingsId,
        transactionType,
        isMemoEntry,
        referenceTransactionUri,
    })

    return [
        {
            fa5_icon: "pencil-alt",
            fa5_icon_family: "fas",

            tip_text: "Edit Transaction",
            icon: "fa-pencil",
            href: editTransactionUrl,
        },
    ]
}

export const pacClassicPacTransactionOptionsSelector = (props) => {
    return [
        // TODO
        // {
        //     fa5_icon: "pencil-alt",
        //     fa5_icon_family: "fas",
        //     tip_text: "Edit Transaction",
        //     icon: "fa-pencil",
        //     // TODO — link to edit form in Pac Classic?
        //     href: "",
        // },
    ]
}

export const campaignFinanceReceiptOptionsSelector = (props) => {
    return []
}

export const campaignFinanceDisbursementOptionsSelector = (props) => {
    return []
}

export const docketOptionsSelector = (props) => [
    {
        ...getStanceOption(props),

        tip_text: "Take a Stance",
        dataCy: "take-a-stance",
        icon: "fa-thumbs-o-up",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: false,
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
    {
        ...getPriorityOption(props),

        tip_text: "Set a Priority",
        icon: "fa-sort-numeric-asc",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: !props.appearanceValues.get("priorityDropDownEnabled"),
                listDropDownEnabled: false,
                issueDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
]

export const articleOptionsSelector = (props) => [
    {
        fa5_icon: "list-ul",
        fa5_icon_family: "fas",

        tip_text: "Lists",
        icon: "fa-list",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: !props.appearanceValues.get("listDropDownEnabled"),
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        ...getStanceOption(props),

        tip_text: "Take a Stance",
        icon: "fa-thumbs-o-up",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                issueDropDownEnabled: false,
                listDropDownEnabled: false,
                priorityDropDownEnabled: false,
                stanceDropDownEnabled: !props.appearanceValues.get("stanceDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
    {
        ...getPriorityOption(props),

        tip_text: "Set a Priority",
        icon: "fa-sort-numeric-asc",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: !props.appearanceValues.get("priorityDropDownEnabled"),
                listDropDownEnabled: false,
                issueDropDownEnabled: false,
            })
            e.stopPropagation()
        },
    },
    {
        fa5_icon: "thumbtack",
        fa5_icon_family: "fas",

        tip_text: "Issues",
        icon: "fa-thumb-tack",
        parentId: props.inlineId,
        callback: (e) => {
            props.updateInlineAppearance(props.uniqueFlvId, props.originalId || props.inlineId, {
                stanceDropDownEnabled: false,
                priorityDropDownEnabled: false,
                listDropDownEnabled: false,
                issueDropDownEnabled: !props.appearanceValues.get("issueDropDownEnabled"),
            })
            e.stopPropagation()
        },
    },
]

export const politicalCommitteeOptionsSelector = (props) => {
    const optionsArray = []

    if (
        checkPermission(DjangIO.app.models.PermissionType.pac_state_and_local_committees) &&
        props.datum.get("organization")
    ) {
        const pacPaths = getPacPaths({
            politicalCommitteeId: props.inlineId,
            ledgerSettingsId: props.ledgerSettingsId,
        })

        optionsArray.push({
            fa5_icon: "pen",
            fa5_icon_family: "far",

            tip_text: props.datum.get("from_grassroots") ? "Edit Grassroots Note" : "Edit Note",
            icon: "fa-pencil",
            href: props.inlineId ? pacPaths._pacPoliticalCommitteesEdit : pacPaths._pacPoliticalCommitteesNew,
            callback: (e) => {
                props.segueToEditPoliticalCommittee(props.inlineId, props.ledgerSettingsId)
                e.stopPropagation()
                e.preventDefault()
            },
        })
    }

    return optionsArray
}

export const feedItemOptionsSelector = (props) => {
    const underlyingQdt =
        props.datum.get("data_type") && DjangIO.app.models.QuorumDataType.by_value(props.datum.get("data_type"))
    const underlyingId = props.formatSelect(props.datum).underlyingItemId
    const feedItemProps = { ...props, underlyingQdt, isFeedItem: true }
    if (underlyingId) {
        feedItemProps.originalId = feedItemProps.inlineId
        feedItemProps.inlineId = underlyingId
    }

    switch (underlyingQdt) {
        case DjangIO.app.models.QuorumDataType.event:
            return eventOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.document:
            return documentOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.note:
            return noteOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.bill:
            return billOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.vote:
            return voteOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.amendment:
            return amendmentOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.file:
            return fileOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.public_organization:
            return organizationOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.person:
        case DjangIO.app.models.QuorumDataType.staffer:
        case DjangIO.app.models.QuorumDataType.member:
        case DjangIO.app.models.QuorumDataType.legislator:
            return officialOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.supporter:
            return supporterOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.regulation:
            return regulationOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.live_transcript_document:
            return liveTranscriptSectionOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.docket:
            return docketOptionsSelector(feedItemProps)
        case DjangIO.app.models.QuorumDataType.political_committee:
            return politicalCommitteeOptionsSelector(feedItemProps)
        default:
            return undefined
    }
}
